import { useState } from "react";
import {
  encodeObjectKey,
  FILE_LISTING_RETRY_INTERVAL_MS,
  FilesListResponse,
  filesListOptions,
  getFileListingRetryInterval,
  isDirectoryNotReady,
  MAX_FILE_LISTING_RETRIES,
} from "files/hooks/useFiles";
import { ProjectFilesProvider, projectFilesKey } from "files/hooks/useProjectFilesContext";
import { ListCard } from "~/components/ui/list-card";
import { useFetcher } from "~/hooks/useFetcher";
import { FILE_BROWSER_API } from "~/lib/fileBrowserApi";
import { useProjectContext } from "~/pages/manifold-nextgen/components/projects/useProjectContext";
import { nextgenProjectFilesPath } from "~/pages/manifold-nextgen/paths";
import { EmbeddedFiles } from "./EmbeddedFiles";

type DirectoryStatus = "pending" | "ready" | "missing";

/**
 * Whether the project's files directory exists. A new project's directory can
 * list as empty, or be denied, until its access policy takes effect, so an empty
 * listing or an error is re-checked on a short interval before it counts as
 * missing.
 */
const useProjectDirectoryStatus = (projectUuid: string): DirectoryStatus => {
  // Empty listings and errors draw from one budget of attempts.
  const [attempts, setAttempts] = useState(0);
  const countAttempt = () => setAttempts((n) => n + 1);
  const { data } = useFetcher<FilesListResponse>(
    `${FILE_BROWSER_API}/files/${encodeObjectKey(projectFilesKey(projectUuid))}`,
    {
      ...filesListOptions,
      // Polls are deduplicated within this window, so it must not exceed the interval.
      dedupingInterval: 0,
      refreshInterval: (latest?: FilesListResponse) =>
        getFileListingRetryInterval(latest, attempts),
      // SWR's polling skips a key whose last fetch failed, so errors are retried
      // here, on the same fixed interval rather than SWR's exponential backoff.
      // `attempts` does not yet include the error being retried.
      onErrorRetry: (_error, _key, _config, revalidate, { retryCount }) => {
        if (attempts + 1 < MAX_FILE_LISTING_RETRIES) {
          setTimeout(() => revalidate({ retryCount }), FILE_LISTING_RETRY_INTERVAL_MS);
        }
      },
      onSuccess: countAttempt,
      onError: countAttempt,
    }
  );
  if (data && !isDirectoryNotReady(data)) {
    return "ready";
  }
  return attempts < MAX_FILE_LISTING_RETRIES ? "pending" : "missing";
};

export const ProjectFiles = () => {
  const { projectId, project } = useProjectContext();
  const status = useProjectDirectoryStatus(project.id);

  if (status !== "ready") {
    return (
      <div className="h-full min-w-[750px] overflow-auto bg-background">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-6 sm:px-6">
          {status === "pending" ? (
            <div role="status" aria-label="Loading files">
              <ListCard loading placeholder={{ className: "h-[34px]", count: 12 }} />
            </div>
          ) : (
            <div className="flex min-h-[300px] items-center justify-center rounded-lg border border-border bg-background p-6 text-center">
              <p className="text-sm">Files are not available for this project.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <ProjectFilesProvider
      value={{ projectId: project.id, basePath: nextgenProjectFilesPath(projectId) }}
    >
      <EmbeddedFiles />
    </ProjectFilesProvider>
  );
};
